import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/SupabaseAuthContext';
import { ContentProvider } from './contexts/SupabaseContentContext';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import ContactButtons from './components/ContactButtons';
import GoogleTagManager from './components/GoogleTagManager';
import CookieConsent from './components/CookieConsent';
import Home from './pages/Home';
import VPSVietnam from './pages/VPSVietnam';
import VPSUS from './pages/VPSUS';
import VPSCanada from './pages/VPSCanada';
import VPSEurope from './pages/VPSEurope';
import VPSSingapore from './pages/VPSSingapore';
import VPSWindows from './pages/VPSWindows';
import VPSForex from './pages/VPSForex';
import VPSAustralia from './pages/VPSAustralia';
import ProxyPrivate from './pages/ProxyPrivate';
import About from './pages/About';
import Contact from './pages/Contact';
import Terms from './pages/Terms';
import Blog from './pages/Blog';
import Documentation from './pages/Documentation';
import BlogPost from './pages/BlogPost';
import DocPost from './pages/DocPost';
import CategoryPage from './pages/CategoryPage';
import DedicatedServer from './pages/DedicatedServer';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminBlogs from './pages/admin/AdminBlogs';
import AdminDocs from './pages/admin/AdminDocs';
import AdminCategories from './pages/admin/AdminCategories';
import AdminEditor from './pages/admin/AdminEditor';
import ImageManager from './pages/admin/ImageManager';
import ContentBatchUpdater from './pages/admin/ContentBatchUpdater';
import NotFound from './pages/NotFound';
import ProtectedRoute from './components/ProtectedRoute';
import { pushEvent } from './utils/gtm';

// ScrollToTop component to handle scrolling to top on route change
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

function App() {
  // Google Tag Manager ID - Replace with your actual GTM ID
  const GTM_ID = 'GTM-WWB5245';
  
  // State to track if analytics is enabled
  const [analyticsEnabled, setAnalyticsEnabled] = useState(() => {
    const consent = localStorage.getItem('cookie-consent');
    return consent === 'accepted';
  });

  // Handle cookie consent acceptance
  const handleCookieAccept = () => {
    setAnalyticsEnabled(true);
    localStorage.setItem('cookie-consent', 'accepted');
    pushEvent('consent_update', { analytics: 'granted', marketing: 'granted' });
  };

  // Handle cookie consent decline
  const handleCookieDecline = () => {
    setAnalyticsEnabled(false);
    localStorage.setItem('cookie-consent', 'declined');
    pushEvent('consent_update', { analytics: 'denied', marketing: 'denied' });
  };

  return (
    <AuthProvider>
      <ContentProvider>
        <Router>
          {/* Google Tag Manager - only load if analytics is enabled */}
          {analyticsEnabled && <GoogleTagManager id={GTM_ID} />}
          
          <ScrollToTop />
          <div className="min-h-screen bg-gray-50">
            <Header />
            <main>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/vps-viet-nam" element={<VPSVietnam />} />
                <Route path="/vps-us" element={<VPSUS />} />
                <Route path="/vps-canada" element={<VPSCanada />} />
                <Route path="/vps-europe" element={<VPSEurope />} />
                <Route path="/vps-chau-au" element={<VPSEurope />} />
                <Route path="/vps-singapore" element={<VPSSingapore />} />
                <Route path="/vps-windows" element={<VPSWindows />} />
                <Route path="/vps-forex" element={<VPSForex />} />
                <Route path="/vps-australia" element={<VPSAustralia />} />
                <Route path="/proxy-private" element={<ProxyPrivate />} />
                <Route path="/thue-may-chu" element={<DedicatedServer />} />
                <Route path="/about" element={<About />} />
                <Route path="/gioi-thieu" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/lien-he" element={<Contact />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/dieu-khoan-su-dung" element={<Terms />} />
                <Route path="/blog" element={<Blog />} />
                
                {/* Category page for blog posts */}
                <Route path="/category/:slug" element={<CategoryPage />} />
                
                {/* Blog posts - URL trực tiếp không có /blog prefix */}
                <Route path="/:slug" element={<BlogPost />} />
                
                {/* Documentation routes - giữ nguyên /docs prefix */}
                <Route path="/docs" element={<Documentation />} />
                <Route path="/docs/:slug" element={<DocPost />} />
                
                {/* Admin routes với URL bảo mật */}
                <Route path="/vmon-admin/login" element={<AdminLogin />} />
                <Route path="/vmon-admin" element={
                  <ProtectedRoute>
                    <AdminDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/vmon-admin/blogs" element={
                  <ProtectedRoute>
                    <AdminBlogs />
                  </ProtectedRoute>
                } />
                <Route path="/vmon-admin/docs" element={
                  <ProtectedRoute>
                    <AdminDocs />
                  </ProtectedRoute>
                } />
                <Route path="/vmon-admin/categories" element={
                  <ProtectedRoute>
                    <AdminCategories />
                  </ProtectedRoute>
                } />
                <Route path="/vmon-admin/editor/:type/:id?" element={
                  <ProtectedRoute>
                    <AdminEditor />
                  </ProtectedRoute>
                } />
                <Route path="/vmon-admin/image-manager" element={
                  <ProtectedRoute>
                    <ImageManager />
                  </ProtectedRoute>
                } />
                <Route path="/vmon-admin/content-updater" element={
                  <ProtectedRoute>
                    <ContentBatchUpdater />
                  </ProtectedRoute>
                } />
                
                {/* Redirect old admin URLs to 404 */}
                <Route path="/admin/*" element={<NotFound />} />
                
                {/* 404 Route - phải đặt cuối cùng */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
            <Footer />
            <ContactButtons />
            
            {/* Cookie Consent Banner */}
            <CookieConsent 
              onAccept={handleCookieAccept} 
              onDecline={handleCookieDecline} 
            />
          </div>
        </Router>
      </ContentProvider>
    </AuthProvider>
  );
}

export default App;